// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
// Material
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
// RXJS
import { debounceTime, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { fromEvent, merge, Subscription } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
// CRUD
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../core/_base/crud';
// Services and Models
import {
  CategoriesDataSource,
  CategoriesPageRequested,
  Category,
  CategoryActionTypes,
  CategoryService,
  selectCategoryError,
  selectCategoryLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { TranslateService } from '@ngx-translate/core';
import { CategoryAddDialogComponent } from '../category-add/category-add.dialog.component';
import { Router } from '@angular/router';
import { SubheaderService } from '../../../../../../core/_base/layout';
import { AuthService, UserService } from '../../../../../../core/auth/_services';
import { TeamMembersListComponent } from '../team-members-list/team-members-list.component';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { utc } from 'moment';
import { currentUser, User } from '../../../../../../core/auth';
import { TaskService } from '../../../../../../core/timeline';
import { DownloadService } from '../../../../../../shared/utils/download.service';
import { FaxEmailService } from '../../../../../../email-and-fax/services/fax-email.service';
import { CategoryStatusUpdated } from '../../../../../../core/lists/_actions/category.actions';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-categories-list',
  templateUrl: './categories-list.component.html',
  styleUrls: ['./categories-list.component.scss'],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesListComponent implements OnInit, OnDestroy {
  // Table fields
  dataSource: CategoriesDataSource;
  displayedColumns = ['id', 'name', 'mangers', 'role', 'inbox', 'isActive', 'actions'];
  searchBarColumns = [
    'searchId',
    'searchName',
    'searchRole',
    'searchInbox',
    'searchIsActive',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  // Filter fields
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchIdInput') searchIdInput: ElementRef;
  @ViewChild('searchNameInput') searchNameInput: ElementRef;
  getInboxAutocomplete = this.faxEmailService.filterInboxList.bind(this.faxEmailService);
  @ViewChild('searchInboxAutocomplete') searchInboxAutocomplete: GeneralAutocompleteComponent;
  getRoleAutocomplete = this.auth.searchRoles.bind(this.auth);
  @ViewChild('searchRoleAutocomplete') searchRoleAutocomplete: GeneralAutocompleteComponent;
  searchIsActiveSelect = 'true';
  categoriesResult: Category[] = [];
  // Subscriptions
  private subscriptions: Subscription[] = [];
  public title = 'Teams';
  listFlag = null;
  currentUserRoleType: string;
  usersObj = [];
  currentUser;

  constructor(
    private taskService: TaskService,
    public dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private translate: TranslateService,
    private userService: UserService,
    public subheaderService: SubheaderService,
    private auth: AuthService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private categoryService: CategoryService,
    private downloadService: DownloadService,
    private faxEmailService: FaxEmailService,
  ) {}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    this.store.pipe(select(currentUser)).subscribe((res) => (this.currentUser = res));

    this.loadCategoriesList();
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'created';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push(sortSubscription);

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadCategoriesList()))
      .subscribe();
    this.subscriptions.push(paginatorSubscriptions);

    // Filtration, bind to searchInput
    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadCategoriesList();
        }),
      )
      .subscribe();
    this.subscriptions.push(searchSubscription);

    // Init DataSource
    this.dataSource = new CategoriesDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        this.categoriesResult = res;
        const ids = res.map((e) => e.managers.map((ele) => ele.user_id));
        this.taskService.getUsersbyId([...new Set(ids.flat())]).subscribe((result) => {
          this.usersObj = result.pageOfItems;
        });
      });
    this.subscriptions.push(entitiesSubscription);
    const errorSubscription = this.store
      .pipe(select(selectCategoryError), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Update,
            3000,
            true,
            false,
          );
        }
      });
    this.subscriptions.push(errorSubscription);
    const successSubscription = this.store
      .pipe(select(selectCategoryLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case CategoryActionTypes.CategoryCreated:
              successMsg = this.translate.instant('CATEGORY.ADD_SUCCESS');
              msgType = MessageType.Update;
              break;
            case CategoryActionTypes.CategoryUpdated:
              successMsg = this.translate.instant('CATEGORY.UPDATE_SUCCESS');
              msgType = MessageType.Update;
              break;
            case CategoryActionTypes.CategoryStatusUpdated:
              successMsg = this.translate.instant('CATEGORY.UPDATE_SUCCESS');
              msgType = MessageType.Update;
              break;
          }
          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
          }
          this.loadCategoriesList();
        }
      });
    this.subscriptions.push(successSubscription);
    this.currentUserRoleType = this.currentUser.userType;
  }

  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Load Categories List
   */
  loadCategoriesList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'created',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server
    this.store.dispatch(new CategoriesPageRequested({ page: queryParams }));
  }

  /**
   * Returns object for filter
   */
  filterConfiguration(): any {
    const filter: any = {};
    const searchText: string = this.searchInput.nativeElement.value;
    filter.searchData = searchText ? searchText : '';
    filter.searchInbox = this.searchInboxAutocomplete?.value?.id;
    filter.searchId = this.searchIdInput?.nativeElement?.value;
    filter.searchName = this.searchNameInput?.nativeElement?.value;
    filter.searchRole = this.searchRoleAutocomplete?.value?.id;
    filter.isActive = this.searchIsActiveSelect;
    return filter;
  }
  getItemCssClassByStatus(status: boolean): string {
    switch (status) {
      case false:
        return 'danger';
      case true:
        return 'success';
    }
    return '';
  }

  getItemStatusString(status: boolean): string {
    switch (status) {
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return '';
    }
  }
  updateCategoryStatus(category) {
    const _title: string = this.translate.instant('CATEGORY.UPDATE_STATUS_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'CATEGORY.UPDATE_STATUS_SIMPLE.DESCRIPTION',
    );
    const _waitDescription: string = this.translate.instant(
      'CATEGORY.UPDATE_STATUS_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(
        new CategoryStatusUpdated({
          category,
          isActive: !category.isActive,
        }),
      );
    });
  }

  editCategory(category) {
    const managers = category.managers.map((e) => {
      const u = this.usersObj.find((ele) => ele.id === e.user_id);
      if (u) {
        u.hidden = e.is_manager;
        u.is_manager = e.is_manager;
        return u;
      }
    });
    const dialogRef = this.dialog.open(CategoryAddDialogComponent, {
      data: { category, managers },
      width: '450px',
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res.updated) this.loadCategoriesList();
    });
  }

  createCategory() {
    const dialogRef = this.dialog.open(CategoryAddDialogComponent, { width: '450px' });
    dialogRef.afterClosed().subscribe((res) => {
      if (res.created) this.loadCategoriesList();
    });
  }

  teamMembers(category) {
    this.dialog.open(TeamMembersListComponent, {
      data: {
        category_id: category.id,
        category_name: category.name,
      },
      width: '600px',
      disableClose: true,
      closeOnNavigation: true,
    });
  }

  formatRoleOption(role): string {
    return role?.name || '';
  }

  reloadCategoriesList(): void {
    this.paginator.pageIndex = 0;

    this.loadCategoriesList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput.nativeElement.value = '';
    this.searchNameInput.nativeElement.value = '';
    this.searchInboxAutocomplete?.setValue(null);
    this.searchInboxAutocomplete?.updateValueAndValidity();
    this.searchRoleAutocomplete?.setValue(null);
    this.searchRoleAutocomplete?.updateValueAndValidity();
    this.searchIsActiveSelect = 'true';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadCategoriesList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchIdInput.nativeElement.focus());
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadCategoriesList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadCategoriesList();
  }

  exportCategoriesAsCSV(event: Event): void {
    event.stopPropagation();

    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'created',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.categoryService
      .getCategoriesAsCSV(queryParams)
      .pipe(take(1))
      .subscribe((res) => {
        const filename = `${this.title} List [${utc(new Date())
          .local()
          .format('YYYY-MM-DD, hh.mm a')}].csv`;
        this.downloadService.downloadFile(res, filename, 'text/csv');
      });
  }

  getFullNameList(user) {
    const res = '';
    if (!user) {
      return res;
    } else {
      const u = this.usersObj.find((e) => e.id === user.user_id);
      if (u) {
        return '' + u.userName;
      }
      return res;
    }
  }

  getUser(user) {
    const u = this.usersObj.find((e) => e.id === user.user_id);
    if (u) {
      return u;
    }
    return new User();
  }

  getNameList(users) {
    if (!users) {
      return '';
    } else {
      let res = '';
      users = users.slice(1);
      users.forEach((value) => {
        const u = this.usersObj.find((e) => e.id === value.user_id);
        if (u) {
          res = res + '' + u.userName + '\n';
        }
      });
      return res;
    }
  }
}
